import { useCallback, useEffect, useId, useRef, useState, type ComponentType } from 'react';
import { NavLink } from 'react-router-dom';
import { mergeClasses, Tooltip } from '@fluentui/react-components';
import { PanelLeftExpand20Regular, PanelLeftContract20Regular } from '@fluentui/react-icons';
import { useSidebarStyles } from './Sidebar.styles';
import { useUIPreferencesStore } from '@/stores/uiPreferencesStore';
import { getShortcutLabel, IS_MAC } from '@/utils/input';

// Resolved once at module load — depends only on the platform (IS_MAC),
// so there's no reason to recompute this on every Sidebar render.
const TOGGLE_SHORTCUT_LABEL = getShortcutLabel('toggleSidebar');

// Platform-specific aria-keyshortcuts that matches what the matcher
// (`isPrimaryMod`) actually accepts: Meta on Mac, Control elsewhere.
// We deliberately advertise a single chord rather than both: a symmetric
// "Meta+B Control+B" value would tell AT users on Mac that Control+B
// also works, but the matcher only fires on the primary modifier.
const TOGGLE_ARIA_KEYSHORTCUT = IS_MAC ? 'Meta+B' : 'Control+B';

export interface SidebarItem {
  id: string;
  label: string;
  icon: ComponentType;
  /** Route path (relative or absolute, passed to NavLink) */
  path: string;
  /**
   * If true, NavLink will only match this path exactly (no partial / prefix
   * match). Required for items pointing at `/` so they don't appear active
   * for every nested route.
   */
  end?: boolean | undefined;
  /** Override tooltip content in collapsed mode (defaults to `label`). */
  tooltipContent?: string | undefined;
  /** If true, render as a non-interactive disabled state with a tooltip. */
  disabled?: boolean | undefined;
  /** Tooltip text shown when disabled (e.g., permission reason). */
  disabledReason?: string | undefined;
}

export interface SidebarSection {
  id: string;
  /**
   * Optional label. When omitted, no `<h2>` heading is rendered and the
   * section has no divider above it — useful for a primary "Home" item
   * that sits above the labelled groups.
   */
  label?: string | undefined;
  /**
   * Optional icon rendered to the left of the section heading text.
   * Only meaningful when `label` is also set — collapsed sidebars hide
   * the heading entirely.
   */
  icon?: ComponentType | undefined;
  /**
   * Optional override color for the collapsed section icon.
   * When true, applies brand foreground color to the icon.
   */
  brandIcon?: boolean | undefined;
  /**
   * When true, items in this section are indented to visually nest under
   * the heading. Use for sections that represent a parent container
   * (e.g. a project) holding its own features.
   */
  indentItems?: boolean | undefined;
  /**
   * Optional category label rendered above the section label as a smaller,
   * muted heading (e.g. "Product" above the project name).
   */
  categoryLabel?: string | undefined;
  items: SidebarItem[];
}

export interface SidebarProps {
  /** Accessible name for the <aside> landmark. */
  ariaLabel: string;
  /** Accessible name for the <nav> inside the aside. */
  navAriaLabel: string;
  sections: SidebarSection[];
  /** Fired when an item is clicked (item.id provided). Useful for telemetry. */
  onItemClick?: ((itemId: string) => void) | undefined;
}

/**
 * Shared application sidebar. Renders a collapsible vertical nav with
 * accessibility-correct section/list semantics and consistent animation polish.
 *
 * Collapsed state is persisted globally via `useUIPreferencesStore` so the
 * preference survives route transitions (e.g. project → app) and reloads.
 *
 * Used by both ProjectLayout and AppLayout — keeps nav behavior in one place.
 */
export const Sidebar = ({
  ariaLabel,
  navAriaLabel,
  sections,
  onItemClick,
}: SidebarProps) => {
  const styles = useSidebarStyles();
  const collapsed = useUIPreferencesStore((s) => s.sidebarCollapsed);
  const toggleSidebar = useUIPreferencesStore((s) => s.toggleSidebar);
  const sidebarId = useId();
  const toggleTooltip = `${collapsed ? 'Expand sidebar' : 'Collapse sidebar'} (${TOGGLE_SHORTCUT_LABEL})`;

  return (
    <aside
      id={sidebarId}
      className={mergeClasses(styles.sidebar, collapsed ? styles.collapsed : styles.expanded)}
      aria-label={ariaLabel}
    >
      <nav className={mergeClasses(styles.nav, collapsed && styles.navCollapsed)} aria-label={navAriaLabel}>
        {sections.map((section, sectionIdx) => (
          <SidebarSectionGroup
            key={section.id}
            section={section}
            sectionIdx={sectionIdx}
            sidebarId={sidebarId}
            collapsed={collapsed}
            styles={styles}
            onItemClick={onItemClick}
          />
        ))}
      </nav>
      <div className={styles.toggleRow}>
        <Tooltip content={toggleTooltip} relationship="label" positioning="after">
          <button
            type="button"
            className={styles.toggleButton}
            onClick={toggleSidebar}
            aria-expanded={!collapsed}
            aria-controls={sidebarId}
            // Reflects what `isPrimaryMod` actually accepts — see the
            // TOGGLE_ARIA_KEYSHORTCUT constant at the top of this file.
            aria-keyshortcuts={TOGGLE_ARIA_KEYSHORTCUT}
          >
            <span className={styles.toggleIconStack} aria-hidden="true">
              <span className={mergeClasses(styles.toggleIcon, !collapsed && styles.toggleIconVisible)}>
                <PanelLeftContract20Regular />
              </span>
              <span className={mergeClasses(styles.toggleIcon, collapsed && styles.toggleIconVisible)}>
                <PanelLeftExpand20Regular />
              </span>
            </span>
          </button>
        </Tooltip>
      </div>
    </aside>
  );
};

interface SidebarSectionGroupProps {
  section: SidebarSection;
  sectionIdx: number;
  sidebarId: string;
  collapsed: boolean;
  styles: ReturnType<typeof useSidebarStyles>;
  onItemClick?: ((itemId: string) => void) | undefined;
}

const useIsOverflowing = (text: string, disabled: boolean) => {
  const labelRef = useRef<HTMLSpanElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const previousOverflowingRef = useRef(false);

  const checkOverflow = useCallback(() => {
    const el = labelRef.current;
    const nextOverflowing = !disabled && el ? el.scrollWidth > el.clientWidth + 1 : false;

    if (nextOverflowing === previousOverflowingRef.current) return;

    setIsOverflowing(nextOverflowing);
    previousOverflowingRef.current = nextOverflowing;
  }, [disabled]);

  useEffect(() => {
    checkOverflow();
  }, [text, checkOverflow]);

  useEffect(() => {
    const el = labelRef.current;
    if (!el) return;

    const observer = new ResizeObserver(checkOverflow);
    observer.observe(el);
    return () => observer.disconnect();
  }, [checkOverflow]);

  return { labelRef, isOverflowing };
};

const SidebarSectionGroup = ({
  section,
  sectionIdx,
  sidebarId,
  collapsed,
  styles,
  onItemClick,
}: SidebarSectionGroupProps) => {
  const headingId = `${sidebarId}-section-${section.id}-label`;
  // An unlabelled section (e.g. the top "Home" group) renders no
  // heading and no divider — it sits flush at the top. We also
  // omit aria-label so the <section> stays as generic content
  // rather than an unhelpful "home region" landmark; the items
  // inside already carry their own accessible names.
  const hasLabel = Boolean(section.label);
  const hasDivider = hasLabel || Boolean(section.categoryLabel);
  const SectionIcon = section.icon;
  const { labelRef, isOverflowing } = useIsOverflowing(section.label ?? '', collapsed || !hasLabel);

  const heading = hasLabel ? (
    <h2
      id={headingId}
      className={styles.sectionLabel}
      data-otto-section-heading=""
      // When the sidebar is collapsed the heading is visually
      // clipped to zero height; hide it from AT too so screen
      // reader users don't hear group labels for groups they
      // can't see.
      aria-hidden={collapsed || undefined}
    >
      {SectionIcon && (
        <span
          className={mergeClasses(styles.sectionIcon, section.brandIcon && styles.sectionIconBrand)}
          aria-hidden="true"
        >
          <SectionIcon />
        </span>
      )}
      <span ref={labelRef} className={styles.sectionLabelText}>
        {section.label}
      </span>
    </h2>
  ) : null;

  return (
    <section
      {...(hasLabel ? { 'aria-labelledby': headingId } : {})}
      className={mergeClasses(
        styles.section,
        sectionIdx > 0 && hasDivider && styles.sectionDivider,
        sectionIdx > 0 && hasDivider && collapsed && styles.sectionDividerCollapsed,
      )}
    >
      {section.categoryLabel && !collapsed && (
        <span className={styles.categoryLabel} aria-hidden="true">
          {section.categoryLabel}
        </span>
      )}
      {hasLabel && isOverflowing && !collapsed ? (
        <Tooltip
          content={section.label as string}
          relationship="description"
          // The h2 isn't focusable, so this tooltip is a hover-only
          // affordance for users who can't read a truncated label.
          // Fluent's Tooltip handles Escape-to-dismiss + sticks on
          // hover (WCAG 1.4.13) and renders consistently with the
          // rest of the app, where the previous native `title` did
          // not. The section's accessible name still comes from the
          // h2 via `aria-labelledby`.
          withArrow
          positioning="after"
        >
          {heading}
        </Tooltip>
      ) : (
        heading
      )}
      {collapsed && SectionIcon && hasLabel && (
        <Tooltip
          content={section.categoryLabel ? `${section.categoryLabel}: ${section.label}` : (section.label as string)}
          relationship="label"
          positioning="after"
        >
          <span
            className={mergeClasses(styles.collapsedSectionIcon, section.brandIcon && styles.sectionIconBrand)}
            aria-hidden="true"
          >
            <SectionIcon />
          </span>
        </Tooltip>
      )}
      <ul
        role="list"
        className={mergeClasses(
          styles.itemList,
          section.indentItems && !collapsed && styles.itemListIndented,
        )}
      >
        {section.items.map((item) => (
          <li key={item.id}>
            <SidebarNavItem item={item} collapsed={collapsed} styles={styles} onItemClick={onItemClick} />
          </li>
        ))}
      </ul>
    </section>
  );
};

interface SidebarNavItemProps {
  item: SidebarItem;
  collapsed: boolean;
  styles: ReturnType<typeof useSidebarStyles>;
  onItemClick?: ((itemId: string) => void) | undefined;
}

const SidebarNavItem = ({ item, collapsed, styles, onItemClick }: SidebarNavItemProps) => {
  const Icon = item.icon;

  const itemClass = collapsed
    ? mergeClasses(styles.navItemBase, styles.collapsedItem)
    : mergeClasses(styles.navItemBase, styles.navItem);

  // Disabled (non-interactive) item — rendered as a span with aria-disabled.
  if (item.disabled) {
    const tooltipContent = item.disabledReason ?? item.label;
    const ariaLabel = collapsed
      ? `${item.label}${item.disabledReason ? ` — ${item.disabledReason}` : ''}`
      : undefined;
    return (
      <Tooltip
        content={tooltipContent}
        relationship={collapsed ? 'label' : 'description'}
        positioning="after"
      >
        <span
          className={mergeClasses(itemClass, styles.navItemDisabled)}
          aria-disabled="true"
          aria-label={ariaLabel}
        >
          <span className={mergeClasses(styles.navIcon)} aria-hidden="true">
            <Icon />
          </span>
          <span
            className={styles.navLabel}
            data-otto-sidebar-label=""
            aria-hidden={collapsed || undefined}
          >
            {item.label}
          </span>
        </span>
      </Tooltip>
    );
  }

  const link = (
    <NavLink
      to={item.path}
      end={item.end ?? false}
      // aria-label only when there's no visible text (collapsed/icon-only).
      // Expanded items use the visible label as the accessible name.
      aria-label={collapsed ? item.label : undefined}
      className={({ isActive }) => mergeClasses(itemClass, isActive && styles.navItemActive)}
      onClick={() => onItemClick?.(item.id)}
    >
      {({ isActive }) => (
        <>
          <span
            className={mergeClasses(styles.navIcon, isActive && styles.navIconActive)}
            aria-hidden="true"
          >
            <Icon />
          </span>
          <span
            className={styles.navLabel}
            data-otto-sidebar-label=""
            aria-hidden={collapsed || undefined}
          >
            {item.label}
          </span>
        </>
      )}
    </NavLink>
  );

  // Collapsed (icon-only) items get a tooltip labelling them.
  return collapsed ? (
    <Tooltip content={item.tooltipContent ?? item.label} relationship="label" positioning="after">
      {link}
    </Tooltip>
  ) : (
    link
  );
};
